import {
  FORWARD_ACCEL,
  PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION,
  SPEED_UNIT_TO_MAP_MPS,
  THRUSTER_SPEED_CAP_MPS
} from '../../shared/constants.js';
import { t } from '../i18n.js';
import type { ControlInput, GameSnapshot } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import {
  appendDialNotches,
  clamp,
  createCard,
  createPlusMinusControl
} from '../controllerUiHelpers.js';

type CreatePilotRoomControllerOptions = {
  controls: HTMLDivElement | null;
  leftBottom: HTMLDivElement | null;
  rightAux: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
};

const moveToward = (current: number, target: number, maxDelta: number): number => {
  const diff = target - current;
  if (Math.abs(diff) <= maxDelta) {
    return target;
  }
  return current + Math.sign(diff) * maxDelta;
};

const quantizePilotEngineThrottle = (value: number): number => {
  const clampedValue = clamp(value, -1, 1);
  return Math.round(clampedValue * PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION) / PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION;
};

export const createPilotRoomController = ({
  controls,
  leftBottom,
  rightAux,
  emitControl
}: CreatePilotRoomControllerOptions) => {
  let pilotPortActualNeedle: HTMLDivElement | null = null;
  let pilotPortTargetNeedle: HTMLDivElement | null = null;
  let pilotPortActualValue: HTMLElement | null = null;
  let pilotPortTargetValue: HTMLElement | null = null;
  let pilotStbdActualNeedle: HTMLDivElement | null = null;
  let pilotStbdTargetNeedle: HTMLDivElement | null = null;
  let pilotStbdActualValue: HTMLElement | null = null;
  let pilotStbdTargetValue: HTMLElement | null = null;
  let pilotPortActualThrottle = 0;
  let pilotStbdActualThrottle = 0;
  let dockedBanner: HTMLElement | null = null;
  let flightReadout: HTMLElement | null = null;

  /** Helm is locked while docked: grey every control out and say why. */
  const applyDockedState = (docked: boolean): void => {
    if (dockedBanner) dockedBanner.hidden = !docked;
    controls?.classList.toggle('pilot-docked', docked);
    for (const zone of [leftBottom, rightAux]) {
      zone?.querySelectorAll('button').forEach((button) => { button.disabled = docked; });
      zone?.classList.toggle('pilot-zone-disabled', docked);
    }
  };


  /** Undo the helm lock's styling without touching button state (other rooms own their own buttons). */
  const clearDockedState = (): void => {
    controls?.classList.remove('pilot-docked');
    for (const zone of [leftBottom, rightAux]) zone?.classList.remove('pilot-zone-disabled');
  };

  const updatePanel = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }

    // updatePanel runs for every snapshot whatever room is open, but the side zones are shared by all
    // rooms: only lock them while the helm itself is on screen (its banner is in the page), otherwise
    // docking greyed out every other room's buttons.
    if (dockedBanner?.isConnected) {
      applyDockedState(snapshot.submarine.docked);
    } else {
      clearDockedState();
    }

    const targetPort = quantizePilotEngineThrottle(snapshot.submarine.leftEngineThrottle);
    const targetStbd = quantizePilotEngineThrottle(snapshot.submarine.rightEngineThrottle);
    const lagStep = (FORWARD_ACCEL * SPEED_UNIT_TO_MAP_MPS) / Math.max(THRUSTER_SPEED_CAP_MPS, 0.0001);

    pilotPortActualThrottle = clamp(moveToward(pilotPortActualThrottle, targetPort, lagStep), -1, 1);
    pilotStbdActualThrottle = clamp(moveToward(pilotStbdActualThrottle, targetStbd, lagStep), -1, 1);

    const toDialAngle = (throttle: number): number => -130 + ((clamp(throttle, -1, 1) + 1) * 0.5 * 260);
    const formatThrottleValue = (throttle: number): string => {
      const magnitude = Math.round(Math.abs(throttle) * PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION);
      const prefix = throttle < -0.001 ? '-' : '';
      return `${prefix}${String(magnitude).padStart(2, '0')}`;
    };

    if (pilotPortActualNeedle && pilotPortTargetNeedle) {
      pilotPortActualNeedle.style.transform = `translateX(-50%) rotate(${toDialAngle(pilotPortActualThrottle)}deg)`;
      pilotPortTargetNeedle.style.transform = `translateX(-50%) rotate(${toDialAngle(targetPort)}deg)`;
    }
    if (pilotStbdActualNeedle && pilotStbdTargetNeedle) {
      pilotStbdActualNeedle.style.transform = `translateX(-50%) rotate(${toDialAngle(pilotStbdActualThrottle)}deg)`;
      pilotStbdTargetNeedle.style.transform = `translateX(-50%) rotate(${toDialAngle(targetStbd)}deg)`;
    }

    if (pilotPortActualValue) {
      pilotPortActualValue.textContent = formatThrottleValue(pilotPortActualThrottle);
    }
    if (pilotPortTargetValue) {
      pilotPortTargetValue.textContent = formatThrottleValue(targetPort);
    }
    if (pilotStbdActualValue) {
      pilotStbdActualValue.textContent = formatThrottleValue(pilotStbdActualThrottle);
    }
    if (pilotStbdTargetValue) {
      pilotStbdTargetValue.textContent = formatThrottleValue(targetStbd);
    }

    // Pitch / current / shaft clearance line: what the pilot needs for tunnels, shafts and surges.
    if (flightReadout) {
      const sub = snapshot.submarine;
      const parts = [`Pitch ${(sub.pitch ?? 0) >= 0 ? '+' : ''}${(sub.pitch ?? 0).toFixed(0)}°`];
      const flow = sub.currentFlow;
      const flowMps = flow ? Math.hypot(flow.x, flow.z) : 0;
      if (flowMps >= 0.05) parts.push(`Current ${flowMps.toFixed(2)} m/s`);
      if (sub.clearance) {
        parts.push(`Up ${sub.clearance.up.toFixed(0)} m`, `Down ${sub.clearance.down.toFixed(0)} m`, `Ahead ${sub.clearance.forward.toFixed(0)} m`);
      }
      flightReadout.textContent = parts.join('  |  ');
    }

  };

  const render = (): void => {
    if (!controls) {
      return;
    }

    controls.classList.add('pilot-controls-layout');

    const throttleCard = createCard('Engine throttles', 'Use the left and right engine throttles to steer and drive the submarine.');
    throttleCard.classList.add('pilot-fullwidth-card', 'ctrl-room-bottom-card');
    const throttleLayout = document.createElement('div');
    throttleLayout.className = 'pilot-helm-layout';

    const controlPanel = document.createElement('div');
    controlPanel.className = 'pilot-helm-panel ctrl-room-bottom-controls';
    const initialLeftThrottle = quantizePilotEngineThrottle(controllerState.lastSnapshot?.submarine.leftEngineThrottle ?? 0);
    const initialRightThrottle = quantizePilotEngineThrottle(controllerState.lastSnapshot?.submarine.rightEngineThrottle ?? 0);
    const throttleStep = 1 / PILOT_ENGINE_THROTTLE_POSITIONS_PER_DIRECTION;

    const createThrottleButtons = (
      label: string,
      side: 'left' | 'right',
      initialValue: number,
      onChange: (value: number) => void
    ) => {
      const wrap = document.createElement('div');
      wrap.className = `pilot-throttle-vertical-wrap pilot-throttle-card pilot-throttle-corner-${side}`;

      let currentValue = quantizePilotEngineThrottle(initialValue);
      const applyValue = (value: number, emit = true): void => {
        currentValue = quantizePilotEngineThrottle(clamp(value, -1, 1));
        if (emit) {
          onChange(currentValue);
        }
      };

      applyValue(currentValue, false);

      const buttonControl = createPlusMinusControl(
        label,
        {
          title: `${label} throttle decrease`,
          ariaLabel: `${label} throttle decrease`,
          onStep: () => applyValue(currentValue - throttleStep)
        },
        {
          title: `${label} throttle increase`,
          ariaLabel: `${label} throttle increase`,
          onStep: () => applyValue(currentValue + throttleStep)
        },
        { buttonRowClassName: 'pilot-throttle-button-row pilot-throttle-button-row-only' }
      );

      wrap.append(buttonControl.wrap);
      return { wrap, setValue: (value: number) => applyValue(value, false) };
    };

    const leftThrottleControl = createThrottleButtons('Left', 'left', initialLeftThrottle, (value) => {
      emitControl({ action: 'pilot_left_engine', value });
    });

    const rightThrottleControl = createThrottleButtons('Right', 'right', initialRightThrottle, (value) => {
      emitControl({ action: 'pilot_right_engine', value });
    });

    const allStopBtn = document.createElement('button');
    allStopBtn.type = 'button';
    allStopBtn.className = 'pilot-all-stop-btn';
    allStopBtn.textContent = t('all_stop');
    allStopBtn.addEventListener('click', () => {
      leftThrottleControl.setValue(0);
      rightThrottleControl.setValue(0);
      emitControl({ action: 'pilot_all_stop' });
    });

    const allStopHousing = document.createElement('div');
    allStopHousing.className = 'pilot-all-stop-housing';
    allStopHousing.append(allStopBtn);

    if (leftBottom) {
      leftBottom.innerHTML = '';
      leftBottom.append(leftThrottleControl.wrap);
    }

    if (rightAux) {
      rightAux.innerHTML = '';
      rightAux.append(rightThrottleControl.wrap);
    }

    throttleLayout.append(controlPanel);
    throttleCard.append(throttleLayout);

    const gaugesCard = createCard('Navigation instruments', 'Depth, speed, and heading for the pilot.');
    gaugesCard.classList.add('pilot-fullwidth-card', 'ctrl-room-main-card');
    const gaugesGrid = document.createElement('div');
    gaugesGrid.className = 'pilot-throttle-gauges-grid';

    const createThrottleGauge = (label: 'PORT' | 'STBD') => {
      const wrap = document.createElement('div');
      wrap.className = 'pilot-throttle-gauge-card';

      const dial = document.createElement('div');
      dial.className = 'pilot-throttle-dial';

      const scale = document.createElement('div');
      scale.className = 'pilot-throttle-scale';

      const center = document.createElement('div');
      center.className = 'pilot-throttle-center';

      const labelEl = document.createElement('div');
      labelEl.className = 'pilot-throttle-dial-label';
      labelEl.textContent = label;

      const subtitle = document.createElement('div');
      subtitle.className = 'pilot-throttle-dial-subtitle';
      subtitle.textContent = t('throttle');

      const targetNeedle = document.createElement('div');
      targetNeedle.className = 'pilot-throttle-needle target';

      const actualNeedle = document.createElement('div');
      actualNeedle.className = 'pilot-throttle-needle actual';

      dial.append(scale, targetNeedle, actualNeedle, center, labelEl, subtitle);
      appendDialNotches(dial);

      const digitalRow = document.createElement('div');
      digitalRow.className = 'pilot-throttle-digital-row';

      const actualCell = document.createElement('div');
      actualCell.className = 'pilot-throttle-digital-cell actual';
      const actualLabel = document.createElement('span');
      actualLabel.className = 'pilot-throttle-digital-label';
      actualLabel.textContent = t('actual');
      const actualValue = document.createElement('strong');
      actualValue.className = 'pilot-throttle-digital-value';
      actualValue.textContent = '00';
      actualCell.append(actualLabel, actualValue);

      const targetCell = document.createElement('div');
      targetCell.className = 'pilot-throttle-digital-cell target';
      const targetLabel = document.createElement('span');
      targetLabel.className = 'pilot-throttle-digital-label';
      targetLabel.textContent = t('target');
      const targetValue = document.createElement('strong');
      targetValue.className = 'pilot-throttle-digital-value';
      targetValue.textContent = '00';
      targetCell.append(targetLabel, targetValue);

      digitalRow.append(actualCell, targetCell);
      wrap.append(dial, digitalRow);

      return {
        wrap,
        actualNeedle,
        targetNeedle,
        actualValue,
        targetValue
      };
    };

    const portGauge = createThrottleGauge('PORT');
    const stbdGauge = createThrottleGauge('STBD');

    pilotPortActualNeedle = portGauge.actualNeedle;
    pilotPortTargetNeedle = portGauge.targetNeedle;
    pilotPortActualValue = portGauge.actualValue;
    pilotPortTargetValue = portGauge.targetValue;

    pilotStbdActualNeedle = stbdGauge.actualNeedle;
    pilotStbdTargetNeedle = stbdGauge.targetNeedle;
    pilotStbdActualValue = stbdGauge.actualValue;
    pilotStbdTargetValue = stbdGauge.targetValue;

    const initialPort = quantizePilotEngineThrottle(controllerState.lastSnapshot?.submarine.leftEngineThrottle ?? 0);
    const initialStbd = quantizePilotEngineThrottle(controllerState.lastSnapshot?.submarine.rightEngineThrottle ?? 0);
    pilotPortActualThrottle = initialPort;
    pilotStbdActualThrottle = initialStbd;

    // ALL STOP sits in the gap between the two dials (it used to crowd the left-hand throttle controls).
    gaugesGrid.append(portGauge.wrap, allStopHousing, stbdGauge.wrap);

    flightReadout = document.createElement('div');
    flightReadout.className = 'pilot-flight-readout';
    gaugesCard.append(gaugesGrid, flightReadout);
    controls.append(gaugesCard, throttleCard);

    dockedBanner = document.createElement('div');
    dockedBanner.className = 'pilot-docked-banner';
    dockedBanner.textContent = 'DOCKED — CONTROLS DISABLED';
    dockedBanner.hidden = true;
    controls.append(dockedBanner);

    updatePanel(controllerState.lastSnapshot);
  };

  return {
    render,
    updatePanel
  };
};
